import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { FEATURE_FLAG_KEYS } from '../../../config/growthbook';
import { useActor } from '../../hooks/useActor';
import { canUseAskMoo } from '../utils/ask-moo-access';

/**
 * Whether the current persona may use the Ask Moo experience right now.
 *
 * - **Cowbell**: always on (GA, unflagged).
 * - **Brokers (AGENCY)**, **policyholders (CUSTOMER)**, and **carriers
 *   (CARRIER)**: only when the `ask_moo_brokers_policyholders` GrowthBook flag
 *   is on. This gates the ENTIRE Ask Moo surface for those personas (the
 *   chat-UI mount AND the launcher button) — not just their restricted agent.
 *   The backend separately restricts their agent catalog (Cyber Pulse for
 *   AGENCY/CUSTOMER; the carrier-scoped ML UW Agent for CARRIER — OMNI-484,
 *   OMNI-533). Carriers reuse this flag rather than getting a dedicated one;
 *   split it out if carrier rollout ever needs to move independently of
 *   broker/policyholder rollout.
 * - **All other personas**: never.
 *
 * Both the mount (`ConsoleLayout`) and the launcher (`MainHeader`) consume this
 * single hook so they can't disagree (a mounted-but-unreachable panel, or a
 * dead button).
 */
export function useAskMooEnabled(): boolean {
  const actor = useActor();
  const brokersPolicyholdersEnabled = useFeatureIsOn(
    FEATURE_FLAG_KEYS.ASK_MOO_BROKERS_POLICYHOLDERS
  );
  if (!canUseAskMoo(actor)) return false;
  // Cowbell is GA; every other admitted persona is gated behind the flag.
  return actor.isCowbell || brokersPolicyholdersEnabled;
}
